<template>
  <div class="bulk-page" dir="rtl">
    <div class="bulk-hero">
      <div>
        <p class="hero-kicker mb-1">کاتالوگ فروش</p>
        <h1>تاریخچه ویرایش گروهی</h1>
      </div>
      <div class="bulk-hero-badge">
        <b-icon icon="clock-history"></b-icon>
      </div>
    </div>

    <div class="bulk-toolbar">
      <router-link class="bulk-tool-btn" :to="{ name: 'ProductsDashboard' }">
        <b-icon icon="arrow-right"></b-icon>
        <span>بازگشت به محصولات</span>
      </router-link>
      <button type="button" class="bulk-tool-btn" :disabled="loading" @click="load(page)">
        <b-icon icon="arrow-repeat"></b-icon>
        <span>به‌روزرسانی</span>
      </button>
    </div>

    <div v-if="loading" class="bulk-state">
      <div class="spinner-border" role="status"></div>
      <p class="mb-0 mt-2">در حال بارگذاری...</p>
    </div>
    <div v-else-if="!items.length" class="bulk-state">
      <b-icon icon="clock-history"></b-icon>
      <p class="mb-0">هنوز ویرایش گروهی انجام نشده است.</p>
    </div>
    <div v-else class="bulk-list">
      <article v-for="item in items" :key="item.id" class="bulk-entry" :class="{ 'is-undone': item.status !== 'done' }">
        <div class="bulk-entry-head">
          <span class="bulk-entry-icon"><b-icon :icon="operationIcon(item.operation)"></b-icon></span>
          <div class="bulk-entry-title">
            <strong>{{ operationTitle(item.operation) }} <small>#{{ item.id }}</small></strong>
            <small>{{ formatDate(item.created_at) }} · {{ userName(item) }}</small>
          </div>
          <span class="bulk-chip" :class="{ 'is-undone': item.status !== 'done' }">
            {{ item.status === 'done' ? 'اعمال شده' : 'بازگردانی شده' }}
          </span>
        </div>
        <p class="bulk-entry-desc">{{ describe(item) }}</p>
        <div class="bulk-entry-meta">
          <span>{{ scopeText(item.scope) }}</span>
          <span>{{ formatNumber(item.products_count) }} محصول</span>
          <span v-if="item.variants_count">{{ formatNumber(item.variants_count) }} تنوع</span>
          <span v-if="item.undone_at">بازگردانی: {{ formatDate(item.undone_at) }}</span>
        </div>
        <div class="bulk-entry-actions">
          <button type="button" class="bulk-details-btn" @click="openDetails(item)">
            <b-icon icon="list-check"></b-icon>
            <span>جزئیات</span>
          </button>
          <button v-if="item.status === 'done'" type="button" class="bulk-undo-btn" :disabled="busyId === item.id" @click="undo(item)">
            <b-spinner v-if="busyId === item.id" small></b-spinner>
            <b-icon v-else icon="arrow-counterclockwise"></b-icon>
            <span>بازگردانی</span>
          </button>
        </div>
      </article>
    </div>

    <div class="bulk-pager" v-if="meta && meta.last_page > 1">
      <div class="bulk-toolbar mb-0">
        <button type="button" class="bulk-tool-btn" :disabled="page <= 1 || loading" @click="load(page - 1)">قبلی</button>
        <span class="align-self-center px-2">{{ formatNumber(page) }} / {{ formatNumber(meta.last_page) }}</span>
        <button type="button" class="bulk-tool-btn" :disabled="page >= meta.last_page || loading" @click="load(page + 1)">بعدی</button>
      </div>
    </div>

    <product-bulk-edit-details :bulk-edit="detailsItem"/>
  </div>
</template>

<script>
import {formatNumber} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/Converter";
import {toast} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/Notification";
import ProductBulkEditDetails from "../../components/dashboard/product/ProductBulkEditDetails.vue";
import "../../components/dashboard/product/bulk-edit-ui.css";

export default {
  name: "ProductBulkEdits",
  components: {ProductBulkEditDetails},
  data() {
    return {
      items: [],
      meta: null,
      page: 1,
      loading: true,
      busyId: null,
      detailsItem: null,
    }
  },
  methods: {
    formatNumber,
    openDetails(item) {
      this.detailsItem = item
      this.$nextTick(() => this.$bvModal.show('product-bulk-edit-details'))
    },
    async load(page = 1) {
      this.loading = true
      try {
        const data = await this.$store.dispatch('ProductBulkEdit/fetchHistory', page)
        this.items = Array.isArray(data.data) ? data.data : []
        this.meta = data.meta || null
        this.page = (this.meta && this.meta.current_page) || page
      } catch (e) {
        this.items = []
      } finally {
        this.loading = false
      }
    },
    formatDate(value) {
      if (!value) return ''
      const date = new Date(value)
      if (isNaN(date.getTime())) return value
      return date.toLocaleString('fa-IR', {dateStyle: 'medium', timeStyle: 'short'})
    },
    userName(item) {
      const user = item.user
      if (!user) return 'نامشخص'
      return [user.name, user.last_name].filter(Boolean).join(' ') || `کاربر #${user.id}`
    },
    operationIcon(operation) {
      return {price: 'cash-coin', stock: 'box-seam', status: 'eye'}[operation] || 'sliders'
    },
    operationTitle(operation) {
      return {price: 'تغییر قیمت', stock: 'تغییر موجودی', status: 'تغییر وضعیت انتشار'}[operation] || operation
    },
    scopeText(scope) {
      return {selected: 'انتخاب‌شده‌ها', filter: 'نتایج فیلتر', all: 'همه محصولات'}[scope] || scope
    },
    describe(item) {
      const p = item.params || {}
      if (item.operation === 'price') {
        const amount = p.mode === 'percent' ? `${p.value}٪` : `${formatNumber(p.value)} تومان`
        let text = `${p.direction === 'decrease' ? 'کاهش' : 'افزایش'} ${amount}`
        text += p.offer_mode === 'fixed' ? '، تخفیف ثابت' : '، تخفیف به همان نسبت'
        if (Number(p.rounding_step) > 0) {
          const dir = {up: 'رو به بالا', down: 'رو به پایین', nearest: 'نزدیک‌ترین'}[p.rounding_direction] || ''
          text += `، رُند ${formatNumber(p.rounding_step)} تومان ${dir}`
        }
        return text
      }
      if (item.operation === 'stock') {
        if (p.method === 'adjust') {
          return `${p.adjust_direction === 'decrease' ? 'کاهش' : 'افزایش'} ${formatNumber(p.adjust_amount)} عدد موجودی${p.sync_status ? '، همراه با به‌روزرسانی وضعیت موجودی' : ''}`
        }
        const action = p.action === 'in_stock' ? 'موجود کردن' : 'ناموجود کردن'
        return `${action} (${p.method === 'quantity' ? 'تغییر تعداد' : 'فقط وضعیت'})`
      }
      return Number(p.status) === 1 ? 'انتشار محصولات' : 'خارج کردن از انتشار'
    },
    async undo(item) {
      this.busyId = item.id
      try {
        const check = await this.$store.dispatch('ProductBulkEdit/undoCheck', item.id)
        let message = `عملیات #${item.id} بازگردانی شود؟ مقادیر قبلی ${formatNumber(item.products_count)} محصول برمی‌گردد.`
        if (check.changed_after_count > 0) {
          message = `هشدار: ${formatNumber(check.changed_after_count)} مورد بعد از این ویرایش گروهی دوباره تغییر کرده‌اند و با بازگردانی، آن تغییرات هم از بین می‌روند.\n\n` + message
        }
        if (!confirm(message)) return
        await this.$store.dispatch('ProductBulkEdit/undo', item.id)
        toast('بازگردانی انجام شد', `عملیات #${item.id} بازگردانی شد`)
        await this.load(this.page)
      } catch (e) {
        const data = e && e.response && e.response.data
        toast('خطا', (data && data.message) || 'بازگردانی انجام نشد', 'danger')
      } finally {
        this.busyId = null
      }
    },
  },
  mounted() {
    this.load(1)
  },
}
</script>
